import type { DocumentSlotRole, ProgrammeSigningDocumentRow } from './ProgrammeSigningPage.types';

/**
 * The signature rows, reordered so every row of a document sits together. The API orders by
 * `createdAt asc, id asc` without grouping, and two documents written in one transaction can
 * interleave, which would nest a slot under an unrelated document. Stable both ways, so the
 * slot the server resolves first is still the one shown first.
 */
export const groupSignatureRowsByDocument = (
	documents: ProgrammeSigningDocumentRow[]
): ProgrammeSigningDocumentRow[] => {
	const rowsByInstance = new Map<string, ProgrammeSigningDocumentRow[]>();

	for (const document of documents) {
		const existing = rowsByInstance.get(document.documentWorkflowInstanceId);
		if (existing) {
			existing.push(document);
		} else {
			rowsByInstance.set(document.documentWorkflowInstanceId, [document]);
		}
	}

	return [...rowsByInstance.values()].flat();
};

/**
 * Each signature row's role within its document, keyed by signature id. Two different "firsts",
 * because they can be different rows: `isAdditionalSlot` is the first occurrence whatever its
 * status, so a document never appears twice at the top level; `isBulkSlot` is the first
 * bulk-signable one, so an already-signed slot cannot take the checkbox from a pending sibling.
 * Expects the grouped order from `groupSignatureRowsByDocument`.
 */
export const buildDocumentSlotRoles = (
	documents: ProgrammeSigningDocumentRow[]
): Map<string, DocumentSlotRole> => {
	const seenInstanceIds = new Set<string>();
	const instanceIdsWithBulkSlot = new Set<string>();
	const roles = new Map<string, DocumentSlotRole>();

	for (const document of documents) {
		const { signatureId, documentWorkflowInstanceId, canBulkSign } = document;

		const isAdditionalSlot = seenInstanceIds.has(documentWorkflowInstanceId);
		seenInstanceIds.add(documentWorkflowInstanceId);

		const isBulkSlot = canBulkSign && !instanceIdsWithBulkSlot.has(documentWorkflowInstanceId);
		if (isBulkSlot) {
			instanceIdsWithBulkSlot.add(documentWorkflowInstanceId);
		}

		roles.set(signatureId, { isAdditionalSlot, isBulkSlot });
	}

	return roles;
};
